import { useEffect, useRef, useState } from "react";
import { Globe, Check } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useI18n, SITE_LANGS, VALID_LANG_CODES } from "@/lib/i18n";

// Clean curated language picker — replaces the old Google Translate widget.
// Click the trigger to open a 12-language menu, instant client-side switch.
// Persists choice in localStorage and updates <html lang> for SEO/a11y.
//
// URL sync (added v41): when the user picks a language, we ALSO rewrite the
// URL to `/<lang><path>` (or strip the prefix for English). This is critical
// for SEO — Google indexes per-URL, so `/de/free-weather-widget` ranks on
// google.de while `/free-weather-widget` ranks on google.com. The prerender
// pipeline already ships a static HTML for every (lang × route) pair, so
// the new URL lands on the correctly-prerendered HTML on first byte.
export default function LanguagePicker({ className = "" }) {
  const { lang, setLang } = useI18n();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onEsc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onEsc);
    return () => { document.removeEventListener("mousedown", onClick); document.removeEventListener("keydown", onEsc); };
  }, [open]);

  const current = SITE_LANGS.find((l) => l.code === lang) || SITE_LANGS[0];

  /** Strip any leading /<lang> prefix and re-add the target prefix.
   * Keeps query string + hash + trailing slash so deep links + anchors
   * survive the switch. English drops the prefix entirely. */
  const pathForLang = (target) => {
    const path = location.pathname || "/";
    const segs = path.split("/").filter(Boolean);
    if (segs.length && VALID_LANG_CODES.has(segs[0])) segs.shift();
    const rest = segs.length ? "/" + segs.join("/") : "/";
    const next = target === "en" ? rest : `/${target}${rest === "/" ? "/" : rest}`;
    return next + (location.search || "") + (location.hash || "");
  };

  const handlePick = (code) => {
    setLang(code);
    setOpen(false);
    const target = pathForLang(code);
    // Only navigate if path actually differs (avoids needless re-render).
    if (target !== location.pathname + (location.search || "") + (location.hash || "")) {
      navigate(target);
    }
  };

  return (
    <div ref={ref} className={`relative ${className}`} data-testid="language-picker">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1.5 text-xs font-medium text-zinc-700 hover:text-zinc-900 border border-zinc-300 hover:border-zinc-900 transition-colors px-2.5 py-1.5"
        aria-haspopup="listbox"
        aria-expanded={open}
        data-testid="language-picker-trigger"
        title="Choose language"
      >
        <Globe size={13} strokeWidth={2} />
        <span className="font-mono uppercase tracking-widest">{current.code}</span>
      </button>
      {open && (
        <ul
          role="listbox"
          className="absolute right-0 mt-1.5 z-50 bg-white border border-zinc-200 shadow-xl min-w-[180px] max-h-80 overflow-y-auto"
          data-testid="language-picker-menu"
        >
          {SITE_LANGS.map((l) => {
            const active = l.code === lang;
            return (
              <li key={l.code}>
                <button
                  type="button"
                  role="option"
                  aria-selected={active}
                  onClick={() => handlePick(l.code)}
                  className={`w-full text-left px-3 py-2 flex items-center gap-2 text-sm hover:bg-zinc-50 transition-colors ${active ? "bg-klein/5 text-klein font-semibold" : "text-zinc-800"}`}
                  data-testid={`language-option-${l.code}`}
                >
                  <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-400 w-6">{l.code}</span>
                  <span className="flex-1">{l.native}</span>
                  {active && <Check size={13} className="text-klein shrink-0" />}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
